"use client";
import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { ButtonVariant, RegularButton } from "@/stories/button/button";
import CopyToDashboard from "@/app/assets/icons/copy-to-dashboard";
import { PendingOrders } from "@/app/alts/unlisted-shares/[...id]/page-client";
import LoadingIcon from "@/app/assets/icons/loading";
import useMobile from "@/app/hooks/use-mobile";
import toast, { Toaster } from "react-hot-toast";

interface CancellationModalProps {
    onClose: (value: boolean) => void;
    cancelPendingOrders: () => Promise<boolean>;
    pendingOrders: PendingOrders[];
}

const PendingTransactionWeb: React.FC<CancellationModalProps> = ({
    onClose,
    cancelPendingOrders,
    pendingOrders,
}) => {
    const { isMobile } = useMobile();
    const [isCancelling, setisCancelling] = useState<boolean>(false);
    const onClosePopup = () => {
        onClose(false);
    };

    const notify = () => toast.success("Order id copied to clipboard!");

    const error = () => toast.error("Failed to cancel pending orders. Please try again later.");

    const handleCancelAll = async () => {
        setisCancelling(true);
        const result: boolean = await cancelPendingOrders();
        if (!result) {
            error();
            setisCancelling(false);
            return;
        }
    };

    const handleCopyLink = async (productCode: string) => {
        await navigator.clipboard.writeText(productCode);
        notify();
    };

    return (
        <>
            <Dialog open={true}>
                <DialogDescription />
                <DialogContent className="max-w-[90%] !rounded-3xl border-0 p-0 pb-[42px] pt-[48px] shadow-xl md:h-[684px] md:max-w-[661px] [&>button:last-child]:hidden">
                    <VisuallyHidden.Root>
                        <DialogTitle>Pending Transactions ({pendingOrders.length})</DialogTitle>
                    </VisuallyHidden.Root>

                    <div className="flex h-full flex-col">
                        <p className="!min-h-[85px] px-4 text-center text-xl font-semibold text-black md:px-0 md:pb-4 md:text-3xl">
                            To unlock your 1st transaction benefit, please <br /> cancel the pending
                            transaction
                        </p>

                        <div className="my-6 bg-[#94A3B8] p-4 md:my-0 md:py-4">
                            <p className="pl-1 text-xl font-medium text-white md:pl-[38px]">
                                Pending transaction ({pendingOrders.length})
                            </p>
                        </div>

                        <div className="max-h-[140px] flex-1 overflow-y-auto bg-white px-[21.5px] md:max-h-[360px] md:px-6 md:py-8 md:pl-[56px] md:pr-[48px]">
                            <div className="md:space-y-4">
                                {pendingOrders?.map((txn, index) => (
                                    <div key={index} className="flex flex-col justify-between">
                                        <div className="flex">
                                            <span className="truncate text-base font-semibold leading-[26px] text-gray-900 md:max-w-[340px]">
                                                {txn.name}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between text-right md:justify-start md:gap-14">
                                            <div className="py-2 text-xs font-medium text-neutral-300 md:text-sm">
                                                {txn.date}
                                            </div>
                                            <span className="text-xs font-medium text-neutral-300 md:text-sm">
                                                {txn.amount}
                                            </span>
                                            <div className="min-w-[50px] max-w-[50px] truncate text-center text-xs font-medium text-neutral-300 md:min-w-[80px] md:max-w-[80px] md:text-sm">
                                                {txn.mode ?? "Pending"}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div
                                                    className="flex cursor-pointer items-center gap-s5"
                                                    onClick={() =>
                                                        txn.orderId && handleCopyLink(txn.orderId)
                                                    }
                                                >
                                                    <CopyToDashboard size={isMobile ? 14 : 16} />{" "}
                                                    <span className="max-w-[70px] truncate text-xs text-neutral-300 md:max-w-[148px] md:text-sm">
                                                        {txn.orderId}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex w-full items-center">
                                            <hr className="my-2 w-full border-t border-gray-200" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="m-auto flex flex-col-reverse gap-4 pt-6 md:flex-row md:pt-0">
                            <RegularButton
                                variant={ButtonVariant.SecondaryDefault}
                                className="text-base md:mt-8 md:min-h-12 md:min-w-[207px]"
                                label="Proceed without benefits"
                                noIcon
                                showRightIcon={false}
                                onClick={onClosePopup}
                                disabled={isCancelling}
                            />
                            <RegularButton
                                className="!max-w-full text-base md:mt-8 md:min-h-12 md:min-w-[207px]"
                                label={isCancelling ? "" : "Cancel all"}
                                noIcon={!isCancelling ? true : false}
                                icon={
                                    isCancelling ? <LoadingIcon className="animate-spin" /> : <></>
                                }
                                showRightIcon={false}
                                disabled={isCancelling}
                                onClick={handleCancelAll}
                            />
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <Toaster />
        </>
    );
};

export default PendingTransactionWeb;
